import { useMemo, useState, useRef, useEffect } from 'react'
import type { IStoreSelectedFacet } from '@faststore/api'
import { Modal } from 'src/components/common/Modal'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { ChevronUp } from 'src/components/Icons/ChevronUp'

import { AllBrandsModal } from './AllBrandsModal'
import { Checkbox } from '../Checkbox'
import type { Facet } from '../types'

import './brands.scss'

type Props = {
  facet: Facet
  onFacetChange: (value: IStoreSelectedFacet) => void
}

export function BrandFilter({ facet, onFacetChange }: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState(false)
  const [openTab, setOpenTab] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const selectedValues = useMemo(() => {
    return facet.values.filter((value) => value.selected)
  }, [facet])

  function toggleTab() {
    setOpenTab((prev) => !prev)
  }

  const brands = useMemo(() => {
    return facet.values.slice(0, 6)
  }, [facet])

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(openTab ? contentRef.current.scrollHeight : 0)
    }
  }, [openTab, facet])

  return (
    <div className="brand-filter border-b border-gray py-6">
      <button
        className="header-tab-brands-filter flex justify-between items-center w-full"
        onClick={() => toggleTab()}
        tabIndex={0}
        onKeyDown={() => {}}
        aria-expanded={openTab}
      >
        <div className="flex flex-col gap-2">
          <span className="text-base text-restructure-primary font-medium font-inter text-left">
            Marca
          </span>

          {!openTab && (
            <small className="text-xs text-tertiary font-normal font-inter text-left empty:hidden">
              {selectedValues.map((values) => values.label).join(', ')}
            </small>
          )}
        </div>

        {openTab ? <ChevronUp /> : <ChevronDown />}
      </button>

      <div
        className="brands-collapse"
        ref={contentRef}
        style={{
          maxHeight: `${contentHeight}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        <div className="mt-4">
          <ul className="flex flex-wrap gap-[6px] w-full">
            {brands.map((brand) => (
              <Checkbox
                key={brand.value}
                type="default"
                facet={brand}
                onFacetChange={onFacetChange}
                showQuantity={false}
              />
            ))}
            <button
              onClick={() => setIsOpen(true)}
              data-store-label
              data-testid="store-label"
              className="flex gap-1 justify-between sm:justify-start px-4 py-3 text-sm font-medium items-center border rounded-round border-restructure-background-neutral-5 text-[#000]"
            >
              Ver todos <ChevronDown />
            </button>
          </ul>
        </div>

        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          className="rounded max-w-[726px] w-full max-h-[484px] h-full overflow-hidden"
        >
          <AllBrandsModal
            brands={facet.values}
            onFacetChange={onFacetChange}
            onClose={() => setIsOpen(false)}
          />
        </Modal>
      </div>
    </div>
  )
}
